import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowUpRight, ChevronDown } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import Magnetic from './fx/Magnetic';
import { EASE_EXPO, EASE_IN_OUT, EASE_QUINT } from './fx/ease';
import { businesses } from '../data/businesses';

const BOOTCAMP_URL = 'https://events.salt-2.com/';

// ナビは常にサブページへ遷移する（ホーム内スクロールとの二重挙動を廃止し動線を統一）
// 3つの事業は「Services」にまとめ、ホバー・クリックで下に開く（2026-10-17 代表指示）。
// 各事業のLPは新しいタブで開く（同日の代表指示）。
// ホームへはロゴで戻る。Bootcampはナビではなく右端のCTAセルに置く。
// 採用（/careers）は別ヘッダーの採用サイトとして開く。
const SERVICES = businesses.map((b) => ({
  key: b.id,
  en: b.en,
  name: b.id === 'consulting' ? 'コンサル・開発' : b.name,
  headline: b.headline,
  href: b.lp,
  match: b.path,
}));

const NAV = [
  { key: 'services', label: 'Services', jp: 'サービス', short: 'サービス', children: SERVICES },
  { key: 'about', label: 'About', jp: '会社概要', short: '会社概要', path: '/about' },
  { key: 'news', label: 'News', jp: 'ニュース', short: 'ニュース', path: '/news' },
  { key: 'careers', label: 'Careers', jp: '採用サイト', short: '採用', path: '/careers', separate: true },
  { key: 'contact', label: 'Contact', jp: 'お問い合わせ', short: 'お問い合わせ', path: '/contact' },
];

const NAV_ITEM = 'relative flex items-center px-2.5 text-[12px] font-semibold uppercase tracking-label transition-colors duration-300 xl:px-3';

function NavLabel({ item, active, open }) {
  return (
    <>
      <span className="flex flex-col items-center leading-none">
        <span className="flex items-center gap-1">
          {item.label}
          {item.separate && <ArrowUpRight size={11} strokeWidth={2.5} aria-hidden="true" />}
          {item.children && (
            <ChevronDown
              size={12}
              strokeWidth={2.5}
              className={`transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          )}
        </span>
        <span className="mt-1.5 text-[9px] font-medium normal-case tracking-normal text-gray-500">{item.short}</span>
      </span>
      {active && (
        <motion.span
          layoutId="nav-underline"
          className="absolute inset-x-0 bottom-0 h-[3px] bg-live"
          transition={{ duration: 0.5, ease: EASE_EXPO }}
        />
      )}
    </>
  );
}

/**
 * 「Services」: ホバー（マウス）かクリックで、ヘッダーの下に3つの事業を開く。
 * 各事業のLPは新しいタブで開く。Esc・外側のクリック・ページの移動で閉じる。
 */
function ServicesMenu({ item, active }) {
  const reduce = useReducedMotion();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const closeTimer = useRef(null);
  // マウスを乗せて開いた直後のクリックでは閉じない（乗せて開き、そのまま押す人が多いため）
  const openedByHover = useRef(false);

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) openedByHover.current = false;
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    const onDown = (e) => {
      if (!boxRef.current?.contains(e.target)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  const show = () => {
    window.clearTimeout(closeTimer.current);
    if (!open) openedByHover.current = true;
    setOpen(true);
  };
  // 隙間をまたぐ間に閉じないよう、少し待ってから閉じる
  const hide = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 160);
  };

  return (
    // 開いた面はヘッダーの幅いっぱいに出す（この箱は relative にしない）
    <div
      ref={boxRef}
      className="flex items-stretch"
      onPointerEnter={(e) => e.pointerType === 'mouse' && show()}
      onPointerLeave={(e) => e.pointerType === 'mouse' && hide()}
    >
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls="services-menu"
        onClick={() => {
          if (openedByHover.current) {
            openedByHover.current = false;
            setOpen(true);
            return;
          }
          setOpen((v) => !v);
        }}
        className={`${NAV_ITEM} ${active || open ? 'text-ink' : 'text-gray-600 hover:text-ink'}`}
      >
        <NavLabel item={item} active={active} open={open} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            id="services-menu"
            className="absolute inset-x-0 top-full border-b border-line bg-paper shadow-[0_18px_40px_rgba(13,13,13,0.08)]"
            initial={reduce ? false : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: EASE_QUINT }}
          >
            <ul className="mx-auto grid max-w-[1180px] grid-cols-3 border-x border-line">
              {item.children.map((s, i) => (
                <li key={s.key} className={i > 0 ? 'border-l border-line' : ''}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOpen(false)}
                    data-cursor="OPEN"
                    className="group flex h-full flex-col px-7 py-7 transition-colors duration-300 hover:bg-paper-2"
                  >
                    <span className="text-[10px] font-semibold uppercase tracking-label text-gray-500">{s.en}</span>
                    <span className="mt-2 flex items-center gap-2 text-[20px] font-black tracking-display-ja text-ink">
                      {s.name}
                      <ArrowUpRight
                        size={16}
                        className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                        aria-hidden="true"
                      />
                    </span>
                    <span className="mt-2 text-[12.5px] leading-[1.7] text-gray-600">{s.headline}</span>
                    <span className="sr-only">（新しいタブで開きます）</span>
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // オーバーレイ表示中はスクロールを止める
  useEffect(() => {
    if (isMenuOpen) {
      window.__lenis?.stop();
      document.body.style.overflow = 'hidden';
    } else {
      window.__lenis?.start();
      document.body.style.overflow = '';
    }
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const activeKey =
    NAV.find((n) =>
      n.children
        ? n.children.some((c) => location.pathname.startsWith(c.match))
        : n.path && location.pathname.startsWith(n.path)
    )?.key ?? null;

  const overlayNav = (item) => {
    setIsMenuOpen(false);
    if (item.path) navigate(item.path);
  };

  return (
    <>
    <header className="fixed top-0 z-[100] w-full border-b border-line bg-paper/95 backdrop-blur-sm">
      <div className="flex h-[64px] items-stretch md:h-[72px]">
        {/* ロゴセル */}
        <div className="flex items-center border-r border-line px-5 md:px-8 xl:w-[232px]">
          <Magnetic strength={0.2}>
            <Link to="/" className="flex items-center" aria-label="SALT2 ホーム">
              <img src="/icon/SALT2_logo_black.png" alt="SALT2" className="h-6 w-auto" />
            </Link>
          </Magnetic>
        </div>

        {/* ナビ */}
        <nav className="hidden flex-1 items-stretch justify-center gap-2 lg:flex xl:gap-6">
          {NAV.map((item) => {
            const isActive = activeKey === item.key;
            if (item.children) return <ServicesMenu key={item.key} item={item} active={isActive} />;
            return (
              <Link
                key={item.key}
                to={item.path}
                className={`${NAV_ITEM} ${isActive ? 'text-ink' : 'text-gray-600 hover:text-ink'}`}
              >
                <NavLabel item={item} active={isActive} />
              </Link>
            );
          })}
        </nav>

        <div className="flex flex-1 lg:hidden" />

        {/* Bootcamp CTAセル: 現在セクションのアクセント色に連動 */}
        <a
          href={BOOTCAMP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-cursor="APPLY"
          className="group hidden items-center gap-3 border-l border-line bg-live px-8 text-[15px] text-[#F6F4EF] transition-opacity hover:opacity-90 lg:flex"
        >
          <span className="display-type">Bootcamp</span>
          <ArrowUpRight
            size={15}
            className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </a>

        {/* モバイル: CTA + ハンバーガー */}
        <a
          href={BOOTCAMP_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="SALT2 Bootcamp"
          className="flex w-[64px] items-center justify-center border-l border-line bg-live text-[#F6F4EF] lg:hidden"
        >
          <ArrowUpRight size={18} />
        </a>
        <button
          onClick={() => setIsMenuOpen((v) => !v)}
          className="relative flex w-[64px] items-center justify-center border-l border-line text-ink lg:hidden"
          aria-label={isMenuOpen ? 'メニューを閉じる' : 'メニューを開く'}
          aria-expanded={isMenuOpen}
        >
          <span className="relative block h-3 w-6">
            <span
              className={`absolute left-0 top-0 h-[2px] w-full bg-ink transition-transform duration-300 ${
                isMenuOpen ? 'translate-y-[5px] rotate-45' : ''
              }`}
            />
            <span
              className={`absolute bottom-0 left-0 h-[2px] w-full bg-ink transition-transform duration-300 ${
                isMenuOpen ? '-translate-y-[5px] -rotate-45' : ''
              }`}
            />
          </span>
        </button>
      </div>
    </header>

    {/* モバイル: フルスクリーンオーバーレイ。
        headerのbackdrop-filterがfixed子要素の包含ブロックになるため、兄弟要素として置く */}
    <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="fixed inset-x-0 bottom-0 top-[64px] z-[99] flex flex-col overflow-y-auto bg-paper md:top-[72px] lg:hidden"
            initial={reduceMotion ? { opacity: 0 } : { clipPath: 'inset(0 0 100% 0)' }}
            animate={reduceMotion ? { opacity: 1 } : { clipPath: 'inset(0 0 0% 0)' }}
            exit={reduceMotion ? { opacity: 0 } : { clipPath: 'inset(0 0 100% 0)' }}
            transition={{ duration: 0.55, ease: EASE_IN_OUT }}
          >
            <nav className="flex-1 px-6 pt-4">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.key}
                  className="border-b border-line"
                  initial={reduceMotion ? false : { y: 28, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, ease: EASE_EXPO, delay: 0.12 + i * 0.05 }}
                >
                  {item.children ? (
                    // サービス: 3つの事業のLPを新しいタブで開く
                    <div className="py-5">
                      <div className="flex items-baseline justify-between">
                        <span className="flex items-baseline gap-4">
                          <span className="text-[11px] font-semibold tracking-label text-gray-500 tnum">
                            {String(i + 1).padStart(2, '0')}
                          </span>
                          <span className="display-type text-3xl text-ink">{item.label}</span>
                        </span>
                        <span className="text-xs text-gray-500">{item.jp}</span>
                      </div>
                      <ul className="mt-3 pl-9">
                        {item.children.map((s) => (
                          <li key={s.key}>
                            <a
                              href={s.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => setIsMenuOpen(false)}
                              className="flex items-center justify-between gap-3 border-t border-line py-3 text-left"
                            >
                              <span className="text-[16px] font-black tracking-display-ja text-ink">{s.name}</span>
                              <ArrowUpRight size={16} className="text-gray-500" aria-hidden="true" />
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <button
                      onClick={() => overlayNav(item)}
                      className="flex w-full items-baseline justify-between py-5 text-left"
                    >
                      <span className="flex items-baseline gap-4">
                        <span className="text-[11px] font-semibold tracking-label text-gray-500 tnum">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span className="display-type text-3xl text-ink">{item.label}</span>
                        {item.separate && <ArrowUpRight size={16} className="self-center text-gray-500" />}
                      </span>
                      <span className="text-xs text-gray-500">{item.jp}</span>
                    </button>
                  )}
                </motion.div>
              ))}
              <motion.div
                initial={reduceMotion ? false : { y: 28, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, ease: EASE_EXPO, delay: 0.12 + NAV.length * 0.05 }}
                className="py-6"
              >
                <a
                  href={BOOTCAMP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex w-full items-center justify-center gap-3 bg-live py-4 text-[17px] text-[#F6F4EF]"
                >
                  <span className="display-type">SALT2 Bootcamp</span>
                  <ArrowUpRight size={16} />
                </a>
              </motion.div>
            </nav>
            <div className="flex items-center justify-between border-t border-line px-6 py-4 text-[10px] font-semibold uppercase tracking-label text-gray-500">
              <span>Est. 2023 — Tokyo</span>
              <span>Boost Group</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
